import { getCareerHistory } from "@/services/adminService";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Briefcase,
  Wallet,
  History as HistoryIcon,
  UserCheck,
  TrendingUp,
  AlertCircle,
  Loader2,
  Building2,
  BadgeCheck,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const OVERRIDDEN_HELP_TEXT =
  "Include upcoming promotions or salary changes that were cancelled before they took effect, because an earlier-dated change replaced them.";

const CareerHistory = ({ employeeId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeOverridden, setIncludeOverridden] = useState(false);
  const [overriddenHelpOpen, setOverriddenHelpOpen] = useState(false);
  const overriddenHelpTriggerRef = useRef(null);

  useEffect(() => {
    fetchCareerHistory();
  }, [employeeId, includeOverridden]);

  const fetchCareerHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getCareerHistory(employeeId, {
        includeOverridden,
      });
      setHistory(Array.isArray(res) ? res : []);
    } catch (err) {
      setError("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatMoney = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return "—";
    return num.toLocaleString("en-IN");
  };

  const safeText = (v) => (typeof v === "string" && v.trim() ? v : "—");

  const buildChanges = (item) => {
    const changes = [];

    // Designation change
    if (
      item.previous_designation !== undefined ||
      item.new_designation !== undefined
    ) {
      const prev = safeText(item.previous_designation);
      const next = safeText(item.new_designation);

      // Only show if it looks like a real change
      if (prev !== "—" || next !== "—") {
        changes.push({
          key: "designation",
          label: "Designation",
          icon: Briefcase,
          prev,
          next,
          tone: "primary",
        });
      }
    }

    // Department change
    if (
      item.previous_department !== undefined ||
      item.new_department !== undefined
    ) {
      const prev = safeText(item.previous_department);
      const next = safeText(item.new_department);

      if (prev !== "—" || next !== "—") {
        changes.push({
          key: "department",
          label: "Department",
          icon: Building2,
          prev,
          next,
          tone: "primary",
        });
      }
    }

    // Salary change (prefer gross; fall back to net if gross missing)
    const hasGross =
      item.previous_gross_salary !== undefined ||
      item.new_gross_salary !== undefined;
    const hasNet =
      item.previous_net_salary !== undefined ||
      item.new_net_salary !== undefined;

    if (hasGross || hasNet) {
      const prevGross = item.previous_gross_salary;
      const nextGross = item.new_gross_salary;

      const prevNet = item.previous_net_salary;
      const nextNet = item.new_net_salary;

      const showGross =
        Number.isFinite(Number(prevGross)) ||
        Number.isFinite(Number(nextGross));
      const showNet =
        Number.isFinite(Number(prevNet)) || Number.isFinite(Number(nextNet));

      if (showGross) {
        changes.push({
          key: "gross",
          label: "Gross Salary",
          icon: Wallet,
          prev: `₹${formatMoney(prevGross)}`,
          next: `₹${formatMoney(nextGross)}`,
          tone: "success",
        });
      } else if (showNet) {
        changes.push({
          key: "net",
          label: "Net Salary",
          icon: Wallet,
          prev: `₹${formatMoney(prevNet)}`,
          next: `₹${formatMoney(nextNet)}`,
          tone: "success",
        });
      }
    }

    return changes;
  };

  const getEventTypeBadge = (item, changes) => {
    const hasPromotionBits = changes.some(
      (c) => c.key === "designation" || c.key === "department",
    );
    const hasSalaryBits = changes.some(
      (c) => c.key === "gross" || c.key === "net",
    );

    // If you store promotion_type as "promotion" even for combined updates,
    // this still shows "Promotion" + "Salary Update" chips correctly.
    const chips = [];
    if (hasPromotionBits)
      chips.push({ key: "promotion", label: "Promotion", icon: Briefcase });
    if (hasSalaryBits)
      chips.push({ key: "increment", label: "Salary Update", icon: Wallet });

    // fallback if no detected changes
    if (chips.length === 0) {
      chips.push({
        key: "event",
        label: item.promotion_type || "Event",
        icon: BadgeCheck,
      });
    }

    if (item?.is_overridden) {
      chips.push({
        key: "overridden",
        label: "Overridden",
        icon: AlertCircle,
      });
    }

    return chips;
  };

  const sortedHistory = useMemo(() => {
    return history
      .slice()
      .sort(
        (a, b) =>
          new Date(b.effective_from || b.createdAt) -
          new Date(a.effective_from || a.createdAt),
      );
  }, [history]);

  return (
    <div className="w-full bg-background min-h-screen p-2">
      <div>
        <div className="mb-4 flex items-center justify-end gap-2">
          {/* Hover/focus opens via Radix; click/tap toggles so it also works on touch */}
          <TooltipProvider delayDuration={200}>
            <Tooltip
              open={overriddenHelpOpen}
              onOpenChange={setOverriddenHelpOpen}
            >
              <TooltipTrigger asChild>
                <button
                  ref={overriddenHelpTriggerRef}
                  type="button"
                  className="cursor-help rounded-sm text-sm font-medium text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    setOverriddenHelpOpen((open) => !open);
                  }}
                >
                  Show overridden
                </button>
              </TooltipTrigger>
              <TooltipContent
                collisionPadding={16}
                className="max-w-[280px] text-xs leading-relaxed"
                onPointerDownOutside={(e) => {
                  if (overriddenHelpTriggerRef.current?.contains(e.target)) {
                    e.preventDefault();
                  }
                }}
              >
                {OVERRIDDEN_HELP_TEXT}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Switch
            checked={includeOverridden}
            onCheckedChange={setIncludeOverridden}
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 space-y-3">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
              Syncing Records...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center gap-3 p-4 rounded-lg border border-destructive/20 bg-destructive/5 text-destructive mb-6">
            <AlertCircle size={18} />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {/* List */}
        {!loading && !error && sortedHistory.length > 0 && (
          <div className="flex flex-col gap-4">
            {sortedHistory.map((item) => {
              const effectiveDate = item.effective_from || item.createdAt;
              const changes = buildChanges(item);
              const chips = getEventTypeBadge(item, changes);

              return (
                <div
                  key={item._id}
                  className="group relative bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {/* Header Row */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <CalendarIcon size={16} className="text-primary" />
                      </div>

                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-wider">
                          Effective Date
                        </span>
                        <span className="text-sm font-bold text-foreground">
                          {formatDate(effectiveDate)}
                        </span>
                      </div>

                      {/* Chips */}
                      <div className="flex flex-wrap items-center gap-2 ml-2">
                        {chips.map((chip) => {
                          const Icon = chip.icon;
                          const isSalary = chip.key === "increment";
                          const isOverridden = chip.key === "overridden";
                          return (
                            <span
                              key={chip.key}
                              className={[
                                "inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border",
                                isOverridden
                                  ? "bg-destructive/10 text-destructive border-destructive/30"
                                  : isSalary
                                    ? "bg-primary text-white border-none"
                                    : "bg-primary/10 text-primary border-primary/20",
                              ].join(" ")}
                            >
                              <Icon size={12} className="mr-1.5" />
                              {chip.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Verified By */}
                    <div className="flex items-center gap-3 md:justify-end border-t md:border-t-0 pt-4 md:pt-0 border-border/50">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                        <UserCheck size={14} className="text-primary" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-foreground uppercase tracking-tighter">
                          Verified By
                        </span>
                        <span className="text-sm text-muted-foreground uppercase">
                          {safeText(item.approved_by_name)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {item?.is_overridden ? (
                    <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                      This promotion is overridden
                      {item?.overridden_at
                        ? ` on ${formatDate(item.overridden_at)}`
                        : ""}
                      {item?.overridden_reason
                        ? ` (${item.overridden_reason})`
                        : ""}
                      .
                    </div>
                  ) : null}

                  {/* Divider */}
                  <div className="my-4 h-px bg-border/60" />

                  {/* Change Cards */}
                  {changes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {changes.map((c) => {
                        const Icon = c.icon;
                        const isSuccess = c.tone === "success";

                        return (
                          <div
                            key={c.key}
                            className={[
                              "rounded-xl border p-4 flex items-start gap-3",
                              "bg-muted/20",
                              isSuccess
                                ? "border-primary/20"
                                : "border-primary/10",
                            ].join(" ")}
                          >
                            <div
                              className={[
                                "w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
                                isSuccess ? "bg-primary/10" : "bg-secondary/10",
                              ].join(" ")}
                            >
                              <Icon
                                size={16}
                                className={
                                  isSuccess ? "text-primary" : "text-secondary"
                                }
                              />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[10px] font-black text-muted-foreground uppercase tracking-wider">
                                  {c.label}
                                </span>
                              </div>

                              <div className="mt-2 flex items-center gap-2 min-w-0">
                                <span className="text-xs text-muted-foreground truncate">
                                  {c.prev}
                                </span>
                                <ArrowRight
                                  size={12}
                                  className="text-primary shrink-0"
                                />
                                <span className="text-sm font-bold text-foreground truncate">
                                  {c.next}
                                </span>

                                {c.key === "gross" || c.key === "net" ? (
                                  <TrendingUp
                                    size={14}
                                    className="text-primary ml-1 shrink-0"
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No field-level changes captured for this record.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && history.length === 0 && (
          <div className="text-center py-20 border-2 border-dashed border-border rounded-2xl">
            <HistoryIcon
              className="mx-auto text-muted-foreground/20 mb-3"
              size={48}
            />
            <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
              No Career Events Logged
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareerHistory;
